import { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Super Admin → Open Golf settings.
 *
 * Two configurable dates today:
 *   - golf_go_live_date: when the public predictor opens to entrants. Shown
 *     in the demo's GoLiveModal activation copy.
 *   - golf_demo_expiry_date: when ALL Golf demos expire (regardless of when
 *     they were created). Reminder email fires the night before.
 *
 * Add more golf-only knobs (live scoring API config, leaderboard freeze etc)
 * here as they appear.
 */
export default function OpenGolfSettingsPanel({ getAuthHeader }) {
  const [goLiveDate, setGoLiveDate] = useState('');
  const [demoExpiryDate, setDemoExpiryDate] = useState('');
  const [original, setOriginal] = useState({ goLiveDate: '', demoExpiryDate: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    axios
      .get(`${API}/golf/config`)
      .then((r) => {
        const gl = r.data?.go_live_date || '';
        const ex = r.data?.demo_expiry_date || '';
        setGoLiveDate(gl);
        setDemoExpiryDate(ex);
        setOriginal({ goLiveDate: gl, demoExpiryDate: ex });
      })
      .catch(() => setError('Failed to load golf config'))
      .finally(() => setLoading(false));
  }, []);

  const dirty = goLiveDate !== original.goLiveDate || demoExpiryDate !== original.demoExpiryDate;

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const r = await axios.patch(
        `${API}/admin/golf/config`,
        { go_live_date: goLiveDate, demo_expiry_date: demoExpiryDate },
        getAuthHeader(),
      );
      setOriginal({
        goLiveDate: r.data?.go_live_date || goLiveDate,
        demoExpiryDate: r.data?.demo_expiry_date || demoExpiryDate,
      });
      setSavedAt(new Date());
    } catch (e) {
      setError(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const formatPreview = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(`${iso}T00:00:00`);
      const day = d.getDate();
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      const suffix = (n) => {
        if (n >= 11 && n <= 13) return 'th';
        const last = n % 10;
        return last === 1 ? 'st' : last === 2 ? 'nd' : last === 3 ? 'rd' : 'th';
      };
      return `${day}${suffix(day)} ${months[d.getMonth()]} ${d.getFullYear()}`;
    } catch (_) {
      return iso;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-2xl" data-testid="open-golf-settings-panel">
      <div className="flex items-baseline justify-between mb-1">
        <h2 className="text-xl font-bold text-gray-900">Open Golf 2026 Settings</h2>
        <span className="text-xs text-gray-400 uppercase tracking-widest">predictor_type: golf_open_2026</span>
      </div>
      <p className="text-sm text-gray-500 mb-6">Controls the public Open Golf demo behaviour.</p>

      <div className="border-t border-gray-100 pt-6 space-y-6">
        {/* Go-live date */}
        <div>
          <label htmlFor="golf-go-live-date" className="block text-sm font-bold text-gray-900 mb-1">
            Go-live date
          </label>
          <p className="text-xs text-gray-500 mb-3">
            The date the Open Golf predictor opens to entrants. Shown in the demo&apos;s activation modal so clubs know when their predictor will go live. Recommended: ~2 weeks before tournament start.
          </p>
          <input
            id="golf-go-live-date"
            type="date"
            value={goLiveDate}
            onChange={(e) => setGoLiveDate(e.target.value)}
            disabled={loading || saving}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
            data-testid="golf-go-live-date-input"
          />
          <p className="mt-2 text-xs text-gray-600">
            Displays as: <strong className="text-gray-900">{formatPreview(goLiveDate)}</strong>
          </p>
        </div>

        {/* Demo expiry date */}
        <div>
          <label htmlFor="golf-demo-expiry-date" className="block text-sm font-bold text-gray-900 mb-1">
            Demo expiry date
          </label>
          <p className="text-xs text-gray-500 mb-3">
            All Open Golf demos expire at end-of-day on this date (regardless of when created). Reminder email fires the night before. Recommended: 4&ndash;5 days before tournament start.
          </p>
          <input
            id="golf-demo-expiry-date"
            type="date"
            value={demoExpiryDate}
            onChange={(e) => setDemoExpiryDate(e.target.value)}
            disabled={loading || saving}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
            data-testid="golf-demo-expiry-date-input"
          />
          <p className="mt-2 text-xs text-gray-600">
            Demos expire end-of-day on: <strong className="text-gray-900">{formatPreview(demoExpiryDate)}</strong>
          </p>
        </div>

        {/* Save */}
        <div className="pt-2 border-t border-gray-100">
          <button
            type="button"
            onClick={save}
            disabled={!dirty || saving || loading}
            className="px-4 py-2 bg-gray-900 text-white text-sm font-bold rounded-lg disabled:opacity-30 hover:bg-gray-800 transition-colors"
            data-testid="golf-go-live-save"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
          {error && (
            <p className="mt-2 text-xs text-red-600" data-testid="golf-config-error">{error}</p>
          )}
          {savedAt && !dirty && (
            <p className="mt-2 text-xs text-green-600" data-testid="golf-config-saved">Saved {savedAt.toLocaleTimeString()}.</p>
          )}
        </div>
      </div>
    </div>
  );
}
